/**
 * Pure indicator computation from OHLC candles.
 * All functions return {time, value} pairs compatible with lightweight-charts
 * LineSeries.setData(). The array starts at the first bar where the
 * indicator is fully defined (i.e., after the warm-up period).
 */
import type { Candle } from "@/api/types";
import type { UTCTimestamp } from "lightweight-charts";
import {
  deriveRolloverHour,
  sessionDayFromBoundaries,
  type ParsedBoundary,
} from "@/state/selectors/effectiveWeeklyBehaviour";

export interface IndicatorPoint {
  time: UTCTimestamp;
  value: number;
}

export function computeSMA(candles: Candle[], period: number): IndicatorPoint[] {
  if (period < 1 || candles.length < period) return [];
  const result: IndicatorPoint[] = [];
  let sum = 0;
  for (let i = 0; i < period; i++) sum += candles[i].close;
  result.push({ time: candles[period - 1].time as UTCTimestamp, value: sum / period });
  for (let i = period; i < candles.length; i++) {
    sum += candles[i].close - candles[i - period].close;
    result.push({ time: candles[i].time as UTCTimestamp, value: sum / period });
  }
  return result;
}

export function computeEMA(candles: Candle[], period: number): IndicatorPoint[] {
  if (period < 1 || candles.length < period) return [];
  const k = 2 / (period + 1);
  let ema = 0;
  for (let i = 0; i < period; i++) ema += candles[i].close;
  ema /= period;
  const result: IndicatorPoint[] = [
    { time: candles[period - 1].time as UTCTimestamp, value: ema },
  ];
  for (let i = period; i < candles.length; i++) {
    ema = candles[i].close * k + ema * (1 - k);
    result.push({ time: candles[i].time as UTCTimestamp, value: ema });
  }
  return result;
}

export function computeRSI(candles: Candle[], period: number): IndicatorPoint[] {
  if (period < 1 || candles.length < period + 1) return [];
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    if (diff > 0) avgGain += diff;
    else avgLoss -= diff;
  }
  avgGain /= period;
  avgLoss /= period;

  const rsiValue = (g: number, l: number) =>
    l === 0 ? 100 : 100 - 100 / (1 + g / l);

  const result: IndicatorPoint[] = [
    { time: candles[period].time as UTCTimestamp, value: rsiValue(avgGain, avgLoss) },
  ];
  for (let i = period + 1; i < candles.length; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    result.push({ time: candles[i].time as UTCTimestamp, value: rsiValue(avgGain, avgLoss) });
  }
  return result;
}

// ─── Anchor-Time variants (MA / RSI) ───────────────────────────────
//
// These pick a SINGLE calendar anchor — the moment the user selects
// (`anchorDate`; default = start of the previous week) — and compute over
// EVERY bar from that anchor to the present (one expanding window, NO period
// parameter): the count of bars is the anchor→now span.
//   • MA  = the running average of all closes since the anchor.
//   • RSI = the cumulative RSI of all gains/losses since the anchor
//           (= 100·Σgain / (Σgain + Σloss)).
// Bars before the anchor are ignored (no point). The line does NOT reseed.
//
// The Anchored MA also accepts an optional END (`endDate`) so the window can be
// pinned to a fixed span [anchorDate, endDate] — inclusive of the end — for
// backtesting. When omitted the window runs to the present.
//
// ─── Anchor granularity: DAY vs HOUR ───
// Both anchor and end accept two shapes, read in the chart's UTC zone (the
// whole app is UTC — see features/time_handling.md), so the picked wall clock
// IS the anchor with no conversion:
//   • "YYYY-MM-DD"       — DATE-ONLY (legacy). The START HOUR comes from the
//     daily-highlight session boundaries, NOT raw UTC midnight: the picked
//     date's first bar is snapped back to that trading day's session open (the
//     same day-start the daily bands draw), via `sessionDayFromBoundaries`.
//     The end snaps FORWARD to that trading day's last bar. With no boundary
//     data the helper falls back to a plain-UTC weekday, so an empty array is
//     safe.
//   • "YYYY-MM-DDTHH:mm" — HOUR-PRECISE. The anchor is the first bar at/after
//     that exact UTC instant; the end is the last bar at/before it. No session
//     snapping — the hour the user picked is exactly where the window edge sits.

const DAY_MS = 24 * 60 * 60 * 1000;

/** Start of the previous week (Monday 00:00 UTC) as "YYYY-MM-DD", relative to
 *  `refSec`. The default anchor when the user hasn't picked a date. */
export function startOfPreviousWeekDate(refSec: number): string {
  const d = new Date(refSec * 1000);
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  const thisMonMs =
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - dow * DAY_MS;
  const prev = new Date(thisMonMs - 7 * DAY_MS);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${prev.getUTCFullYear()}-${p(prev.getUTCMonth() + 1)}-${p(prev.getUTCDate())}`;
}

/** Fallback rollover hour (UTC) when there are no boundaries yet to derive one
 *  from — the current-season Exness daily open. Boundaries override this the
 *  moment they load, so it's only a first-paint seed (and DST-drifts by ~1h
 *  off-season, which the boundary-derived hour then corrects). */
const FALLBACK_ROLLOVER_HOUR = 22;

/** Default anchor as an HOUR-PRECISE UTC instant ("YYYY-MM-DDTHH:mm"): the
 *  session OPEN of the previous week's Monday. That Monday session opens the
 *  Sunday before at the daily-rollover ("swap") hour — 22:00 now, ~21:00
 *  off-season — so we derive the hour from the tick boundaries (DST-safe) and
 *  land on the Sunday. This matches the daily-highlight day-start, and is what
 *  the old date-only default resolved to under session snapping — now shown
 *  explicitly instead of a misleading 00:00. */
export function defaultAnchorInstant(
  refSec: number,
  boundaries: ParsedBoundary[],
): string {
  const monday = startOfPreviousWeekDate(refSec); // "YYYY-MM-DD"
  const [y, m, d] = monday.split("-").map(Number);
  const sunday = new Date(Date.UTC(y, m - 1, d) - DAY_MS); // session opens Sun
  const hour = deriveRolloverHour(boundaries) ?? FALLBACK_ROLLOVER_HOUR;
  const p = (x: number) => String(x).padStart(2, "0");
  return (
    `${sunday.getUTCFullYear()}-${p(sunday.getUTCMonth() + 1)}-${p(sunday.getUTCDate())}` +
    `T${p(hour)}:00`
  );
}

/**
 * Parse an anchor/end string into the UTC instant it denotes (epoch seconds)
 * plus whether it carried an explicit time-of-day. Everything is read in UTC —
 * the chart's zone — so the picked wall clock is the instant, no conversion.
 *   • "YYYY-MM-DD"       → that day's UTC midnight, `hasTime: false` (the caller
 *     applies session-day snapping).
 *   • "YYYY-MM-DDTHH:mm" → that exact UTC instant, `hasTime: true` (no snapping).
 * Returns null for anything else (empty / malformed).
 */
function parseAnchorInstant(
  value: string,
): { sec: number; hasTime: boolean } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2})?)?$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, hh, mm] = m;
  const hasTime = hh !== undefined;
  const sec = Math.floor(
    Date.UTC(+y, +mo - 1, +d, hasTime ? +hh : 0, hasTime ? +mm : 0) / 1000,
  );
  return { sec, hasTime };
}

/** First candle index whose time is at or after `tsSec` (binary search), or -1. */
function firstAtOrAfter(candles: Candle[], tsSec: number): number {
  let lo = 0;
  let hi = candles.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (candles[mid].time < tsSec) lo = mid + 1;
    else hi = mid;
  }
  return lo < candles.length ? lo : -1;
}

/**
 * Index of the anchor bar for a picked anchor. HOUR-PRECISE anchors
 * ("...THH:mm") land on the first bar at/after that exact UTC instant.
 * DATE-ONLY anchors ("YYYY-MM-DD") take the first bar at/after the date's UTC
 * midnight, then snap BACK to that session-day's open (where the daily-highlight
 * band for the day begins). -1 when the anchor is past all loaded data.
 */
function anchorBarIndex(
  candles: Candle[],
  anchorDate: string,
  boundaries: ParsedBoundary[],
): number {
  const n = candles.length;
  if (n === 0) return -1;

  const parsed =
    parseAnchorInstant(anchorDate) ??
    parseAnchorInstant(startOfPreviousWeekDate(candles[n - 1].time));
  if (!parsed) return -1;

  let i = firstAtOrAfter(candles, parsed.sec);
  if (i < 0) return -1;

  // Hour-precise: the picked instant is exactly where the window starts.
  if (parsed.hasTime) return i;

  // Date-only (legacy): snap back to the start of i's session-day (the daily
  // band's day-open).
  const dayOf = (idx: number) =>
    sessionDayFromBoundaries(candles[idx].time * 1000, boundaries);
  const target = dayOf(i);
  while (i > 0 && dayOf(i - 1) === target) i--;
  return i;
}

/**
 * Last bar index of the window end (inclusive) — the counterpart to
 * `anchorBarIndex`. HOUR-PRECISE ends ("...THH:mm") include every bar at/before
 * that exact UTC instant. DATE-ONLY ends ("YYYY-MM-DD") include the whole
 * trading day the date falls in (walk forward to that session-day's last bar).
 * Returns the final loaded bar when the end is empty/invalid or past all data
 * (open-ended = to the present).
 */
function endBarIndex(
  candles: Candle[],
  endDate: string | undefined,
  boundaries: ParsedBoundary[],
): number {
  const n = candles.length;
  if (n === 0) return -1;
  const parsed = endDate ? parseAnchorInstant(endDate) : null;
  if (!parsed) return n - 1; // empty/invalid → run to present.

  const i = firstAtOrAfter(candles, parsed.sec);
  if (i < 0) return n - 1; // end past all loaded data → run to present.

  // Hour-precise: include the bar exactly at the instant, else stop before it.
  // (-1 when the instant precedes all bars — the caller's end<anchor guard
  // then yields an empty line.)
  if (parsed.hasTime) return candles[i].time === parsed.sec ? i : i - 1;

  const dayOf = (idx: number) =>
    sessionDayFromBoundaries(candles[idx].time * 1000, boundaries);
  // The date's session-day, then walk forward to that day's LAST bar so the
  // whole selected trading day is included.
  const target = dayOf(i);
  let last = i;
  while (last + 1 < n && dayOf(last + 1) === target) last++;
  return last;
}

/** Anchored MA — running average of every close from the anchor to each bar.
 *  No period: the window grows one bar at a time from the anchor. An optional
 *  `endDate` caps the window at the end of that trading day (for backtesting);
 *  when omitted the window runs to the last loaded bar. */
export function computeAnchoredMA(
  candles: Candle[],
  anchorDate: string,
  boundaries: ParsedBoundary[],
  endDate?: string,
): IndicatorPoint[] {
  const n = candles.length;
  if (n === 0) return [];
  const a = anchorBarIndex(candles, anchorDate, boundaries);
  if (a < 0) return [];
  const end = endBarIndex(candles, endDate, boundaries);
  if (end < a) return [];

  const result: IndicatorPoint[] = [];
  let sum = 0;
  for (let b = a; b <= end; b++) {
    sum += candles[b].close;
    result.push({ time: candles[b].time as UTCTimestamp, value: sum / (b - a + 1) });
  }
  return result;
}

/** Anchored RSI — cumulative RSI over every bar from the anchor to each bar.
 *  No period: gains/losses are summed across the whole anchor→now window, so
 *  RSI = 100·Σgain / (Σgain + Σloss). First value is at the bar after the anchor
 *  (the first diff). An optional `endDate` caps the window at the end of that
 *  trading day (for backtesting); when omitted the window runs to the last bar. */
export function computeAnchoredRSI(
  candles: Candle[],
  anchorDate: string,
  boundaries: ParsedBoundary[],
  endDate?: string,
): IndicatorPoint[] {
  const n = candles.length;
  if (n === 0) return [];
  const a = anchorBarIndex(candles, anchorDate, boundaries);
  if (a < 0 || a + 1 >= n) return [];
  const end = endBarIndex(candles, endDate, boundaries);
  if (end <= a) return [];

  const result: IndicatorPoint[] = [];
  let sumGain = 0;
  let sumLoss = 0;
  for (let b = a + 1; b <= end; b++) {
    const diff = candles[b].close - candles[b - 1].close;
    if (diff > 0) sumGain += diff;
    else sumLoss -= diff;
    const denom = sumGain + sumLoss;
    const value = denom === 0 ? 100 : (100 * sumGain) / denom;
    result.push({ time: candles[b].time as UTCTimestamp, value });
  }
  return result;
}
